/**
 * =============================================================================
 * COOKIE MANAGER - Utilitários de Gestão de Consentimento de Cookies
 * =============================================================================
 *
 * Este ficheiro contém a lógica pura de gestão de consentimento:
 * - Tipos e constantes
 * - Leitura/escrita no localStorage
 * - Integração com Google Consent Mode v2
 * - Helpers para verificar categorias autorizadas
 * - Deteção de idioma para i18n
 *
 * Não depende de React — pode ser testado isoladamente.
 */

// ==========================================
// TIPOS
// ==========================================

export type CookieCategory = "necessary" | "analytics" | "marketing" | "preferences";

export interface CookieCategories {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export interface ConsentRecord {
  version: string;
  timestamp: string;
  categories: CookieCategories;
}

export interface CookieService {
  id: string;
  name: string;
  category: CookieCategory;
  description: string;
}

export type ConsentStatus = "granted" | "denied";

export interface GoogleConsentState {
  analytics_storage: ConsentStatus;
  ad_storage: ConsentStatus;
  ad_user_data: ConsentStatus;
  ad_personalization: ConsentStatus;
  functionality_storage: ConsentStatus;
  personalization_storage: ConsentStatus;
  security_storage: ConsentStatus;
}

// ==========================================
// CONSTANTES
// ==========================================

export const CONSENT_VERSION = "1.0.0";
export const STORAGE_KEY = "oficina_cookie_consent";
export const CONSENT_MAX_AGE_DAYS = 365;

// ==========================================
// CATEGORIAS E SERVIÇOS
// ==========================================

export const DEFAULT_CATEGORIES: CookieCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

export const ALL_GRANTED: CookieCategories = {
  necessary: true,
  analytics: true,
  marketing: true,
  preferences: true,
};

export const ALL_DENIED_EXCEPT_NECESSARY: CookieCategories = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

export const COOKIE_SERVICES: CookieService[] = [
  {
    id: "google-analytics",
    name: "Google Analytics",
    category: "analytics",
    description:
      "Ajuda a compreender como os visitantes interagem com o site, recolhendo informação de forma anónima.",
  },
  {
    id: "google-tag-manager",
    name: "Google Tag Manager",
    category: "analytics",
    description:
      "Plataforma que permite gerir tags e scripts de medição de forma centralizada.",
  },
  {
    id: "meta-pixel",
    name: "Meta Pixel",
    category: "marketing",
    description:
      "Utilizado para medir a eficácia de publicidade e criar audiências personalizadas.",
  },
  {
    id: "google-ads",
    name: "Google Ads",
    category: "marketing",
    description:
      "Permite apresentar publicidade personalizada e medir conversões de campanhas.",
  },
  {
    id: "site-preferences",
    name: "Preferências do site",
    category: "preferences",
    description:
      "Memorizam as suas escolhas (como idioma ou tema) para uma experiência personalizada.",
  },
];

// ==========================================
// DECLARAÇÃO GLOBAL PARA GTM/GTAG
// ==========================================

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

// ==========================================
// LOCALSTORAGE
// ==========================================

export function loadConsent(): ConsentRecord | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (!isValidConsentRecord(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsent(
  categories: CookieCategories,
  version: string = CONSENT_VERSION
): ConsentRecord {
  const record: ConsentRecord = {
    version,
    timestamp: new Date().toISOString(),
    categories: { ...categories },
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage pode estar indisponível (modo privado, etc.)
  }
  return record;
}

export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignorar erros de localStorage
  }
}

export function isConsentExpired(record: ConsentRecord, maxAgeDays: number = CONSENT_MAX_AGE_DAYS): boolean {
  const timestamp = new Date(record.timestamp).getTime();
  const now = Date.now();
  const maxAgeMs = maxAgeDays * 24 * 60 * 60 * 1000;
  return now - timestamp > maxAgeMs;
}

export function isConsentVersionOutdated(record: ConsentRecord, currentVersion: string = CONSENT_VERSION): boolean {
  return record.version !== currentVersion;
}

export function hasConsent(record: ConsentRecord | null, category: CookieCategory): boolean {
  if (!record) return category === "necessary";
  return record.categories[category] === true;
}

// ==========================================
// VALIDAÇÃO
// ==========================================

function isValidConsentRecord(value: unknown): value is ConsentRecord {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ConsentRecord>;
  if (typeof record.version !== "string") return false;
  if (typeof record.timestamp !== "string") return false;
  if (!record.categories || typeof record.categories !== "object") return false;
  const categories = record.categories as Partial<CookieCategories>;
  return (
    typeof categories.necessary === "boolean" &&
    typeof categories.analytics === "boolean" &&
    typeof categories.marketing === "boolean" &&
    typeof categories.preferences === "boolean"
  );
}

// ==========================================
// GOOGLE CONSENT MODE v2
// ==========================================

export function getGoogleConsentState(categories: CookieCategories): GoogleConsentState {
  return {
    analytics_storage: categories.analytics ? "granted" : "denied",
    ad_storage: categories.marketing ? "granted" : "denied",
    ad_user_data: categories.marketing ? "granted" : "denied",
    ad_personalization: categories.marketing ? "granted" : "denied",
    functionality_storage: categories.preferences ? "granted" : "denied",
    personalization_storage: categories.preferences ? "granted" : "denied",
    security_storage: "granted",
  };
}

export function ensureDataLayer(): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function (...args: unknown[]) {
    window.dataLayer.push(args);
  };
}

export function setDefaultConsent(categories: CookieCategories): void {
  if (typeof window === "undefined") return;
  ensureDataLayer();
  window.gtag("consent", "default", getGoogleConsentState(categories));
}

export function updateGoogleConsentMode(categories: CookieCategories): void {
  if (typeof window === "undefined") return;
  ensureDataLayer();
  window.gtag("consent", "update", getGoogleConsentState(categories));
  window.dataLayer.push({
    event: "consent_updated",
    consent_state: getGoogleConsentState(categories),
  });
}

export function pushConsentToGTM(categories: CookieCategories): void {
  if (typeof window === "undefined") return;
  ensureDataLayer();
  updateGoogleConsentMode(categories);
}

// ==========================================
// SCRIPTS CONDICIONAIS
// ==========================================

export function loadGTMScript(gtmId: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!gtmId || document.getElementById("gtm-script")) return;

  const script = document.createElement("script");
  script.id = "gtm-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  document.head.appendChild(script);

  // NoScript fallback
  const noscript = document.createElement("noscript");
  const iframe = document.createElement("iframe");
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = "0";
  iframe.width = "0";
  iframe.style.display = "none";
  iframe.style.visibility = "hidden";
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

export function loadGoogleAnalytics(gaId: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (!gaId || document.getElementById("ga-script")) return;

  const script = document.createElement("script");
  script.id = "ga-script";
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);
}

// ==========================================
// I18N
// ==========================================

export type SupportedLanguage = "pt" | "en";

export function detectLanguage(): SupportedLanguage {
  if (typeof window === "undefined") return "pt";
  const htmlLang = document.documentElement.lang;
  if (htmlLang?.startsWith("en")) return "en";
  const navigatorLang = navigator.language;
  if (navigatorLang?.startsWith("en")) return "en";
  return "pt";
}

// ==========================================
// HELPERS
// ==========================================

export function getServicesByCategory(category: CookieCategory): CookieService[] {
  return COOKIE_SERVICES.filter((service) => service.category === category);
}

export function categoryLabel(category: CookieCategory, lang: SupportedLanguage = "pt"): string {
  const labels: Record<CookieCategory, Record<SupportedLanguage, string>> = {
    necessary: { pt: "Necessários", en: "Necessary" },
    analytics: { pt: "Analíticos", en: "Analytics" },
    marketing: { pt: "Marketing", en: "Marketing" },
    preferences: { pt: "Preferências", en: "Preferences" },
  };
  return labels[category][lang];
}

export function categoryDescription(
  category: CookieCategory,
  lang: SupportedLanguage = "pt"
): string {
  const descriptions: Record<CookieCategory, Record<SupportedLanguage, string>> = {
    necessary: {
      pt: "Essenciais para o funcionamento do site. Não podem ser desativados.",
      en: "Essential for the website to function. Cannot be disabled.",
    },
    analytics: {
      pt: "Permitem analisar a utilização do site e melhorar a experiência.",
      en: "Allow us to analyze site usage and improve the experience.",
    },
    marketing: {
      pt: "Utilizados para publicidade personalizada e campanhas.",
      en: "Used for personalized advertising and campaigns.",
    },
    preferences: {
      pt: "Memorizam as suas preferências para uma experiência personalizada.",
      en: "Remember your preferences for a personalized experience.",
    },
  };
  return descriptions[category][lang];
}

export default {
  CONSENT_VERSION,
  STORAGE_KEY,
  CONSENT_MAX_AGE_DAYS,
  DEFAULT_CATEGORIES,
  ALL_GRANTED,
  ALL_DENIED_EXCEPT_NECESSARY,
  COOKIE_SERVICES,
  loadConsent,
  saveConsent,
  clearConsent,
  hasConsent,
  isConsentExpired,
  isConsentVersionOutdated,
  getGoogleConsentState,
  setDefaultConsent,
  updateGoogleConsentMode,
  pushConsentToGTM,
  loadGTMScript,
  loadGoogleAnalytics,
  detectLanguage,
  getServicesByCategory,
  categoryLabel,
  categoryDescription,
};
